<template>
  <div class="page">
    <div class="page-header">
      <div>
        <h1>Bandeau d'annonce</h1>
        <p>Message affiché en haut de l'application client (app.vadimm) pour tous les utilisateurs connectés.</p>
      </div>
    </div>

    <div class="banner-grid">
      <div class="panel">
        <div class="panel__title">
          Message
        </div>
        <div class="panel__body">
          <v-textarea
            v-model="text"
            placeholder="Perturbations sur la génération de contrat : notre équipe est sur le coup."
            :counter="200"
            rows="3"
            auto-grow
            outlined
            :disabled="loading" />

          <div class="banner-row">
            <v-switch
              v-model="activated"
              :label="activated ? 'Bandeau affiché' : 'Bandeau masqué'"
              :disabled="loading"
              inset
              hide-details
              class="mt-0" />
            <v-spacer />
            <v-btn
              color="primary"
              depressed
              :loading="saving"
              :disabled="loading || (activated && !text.trim())"
              @click="save">
              Enregistrer
            </v-btn>
          </div>
          <p
            v-if="activated && !text.trim()"
            class="banner-hint">
            Écris un message pour pouvoir afficher le bandeau.
          </p>
        </div>
      </div>

      <div class="panel">
        <div class="panel__title">
          État actuel sur {{ $env.label }}
        </div>
        <div class="panel__body">
          <div
            v-if="current"
            class="banner-state">
            <span :class="['pill', current.activated && current.event ? 'pill--success' : '']">
              {{ current.activated && current.event ? 'Affiché' : 'Masqué' }}
            </span>
            <span
              v-if="current.created_on"
              class="cell-muted">
              Modifié le {{ current.created_on | dtFormat('DD/MM/YYYY à HH:mm') }}
            </span>
          </div>
          <div
            v-else
            class="cell-muted">
            Chargement…
          </div>
        </div>
      </div>
    </div>

    <div class="panel mt-4">
      <div class="panel__title">
        Aperçu
      </div>
      <div class="panel__body">
        <div class="banner-preview">
          <div
            v-if="text.trim()"
            class="banner-preview__bar">
            {{ text }}
          </div>
          <div class="banner-preview__app">
            <div class="banner-preview__menu" />
            <div class="banner-preview__content">
              <div />
              <div />
              <div />
            </div>
          </div>
        </div>
        <p class="banner-hint mt-3">
          Les utilisateurs déjà connectés le voient en direct ou au plus tard sous 2 minutes.
        </p>
      </div>
    </div>

    <div class="panel mt-4">
      <div class="panel__title">
        Maintenance Vadimm
      </div>
      <div class="panel__body">
        <div class="banner-row">
          <span :class="['pill', maintenance ? 'pill--danger' : 'pill--success']">
            {{ maintenance ? 'Application indisponible' : 'Application disponible' }}
          </span>
          <v-spacer />
          <v-btn
            :color="maintenance ? 'primary' : 'error'"
            depressed
            :loading="savingMaintenance"
            :disabled="loading"
            @click="toggleMaintenance(!maintenance)">
            {{ maintenance ? 'Désactiver la maintenance' : 'Activer la maintenance' }}
          </v-btn>
        </div>
        <p class="banner-hint">
          Activée, l'application client affiche la page de maintenance à tous les utilisateurs.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapState } from 'vuex'

  // Ancienne valeur technique stockée avant que le texte soit éditable : le site client affichait ce message en dur
  const LEGACY_EVENTS = {
    issueContract: 'Perturbations sur la génération de contrat - Notre équipe technique est sur la résolution du problème rencontré.'
  }

  export default {
    name: 'Banner',
    data () {
      return {
        text: '',
        activated: false,
        current: null,
        loading: true,
        saving: false,
        maintenance: false,
        savingMaintenance: false
      }
    },
    computed: {
      ...mapState(['currentUser'])
    },
    created () {
      this.fetch()
    },
    methods: {
      async fetch () {
        this.loading = true
        try {
          // /config est public et contient l'état du bandeau (clé DESKTOP_APP_VERSION)
          const { data } = await this.$api.axios.get('config')
          const config = data.find(x => x.key === 'DESKTOP_APP_VERSION') || {}
          this.current = config.events_service || { event: null, activated: false }
          this.text = LEGACY_EVENTS[this.current.event] || this.current.event || ''
          this.activated = !!this.current.activated
          this.maintenance = !!(config.unavailable_service && config.unavailable_service.activated)
        } catch (e) {
          this.$awn.alert('Impossible de lire l\'état du bandeau')
        } finally {
          this.loading = false
        }
      },
      async toggleMaintenance (value) {
        if (value) {
          const ok = await this.$confirm({
            title: 'Activer la maintenance ?',
            message: `L'application client sera indisponible pour tous les utilisateurs (${this.$env.label}).`,
            confirmLabel: 'Activer',
            danger: true
          })
          if (!ok) return
        }
        this.savingMaintenance = true
        try {
          await this.$api.axios.patch(`unavailable-service/${this.currentUser.pk}`, {
            unavailable_service: { activated: value, created_on: null }
          })
          this.maintenance = value
          this.$awn.success(value ? 'Maintenance activée' : 'Maintenance désactivée')
        } catch (e) {
          if (e.message !== 'Action annulée') this.$awn.alert('Impossible de modifier la maintenance')
        } finally {
          this.savingMaintenance = false
        }
      },
      async save () {
        this.saving = true
        try {
          const { data } = await this.$api.axios.patch(`event-service/${this.currentUser.pk}`, {
            events_service: { event: this.text.trim() || null, activated: this.activated }
          })
          this.$awn.success(this.activated ? 'Bandeau affiché' : 'Bandeau masqué')
          if (data && data.event !== undefined) {
            this.current = data
          } else {
            await this.fetch()
          }
        } catch (e) {
          if (e.message !== 'Action annulée') this.$awn.alert('Impossible d\'enregistrer le bandeau')
        } finally {
          this.saving = false
        }
      }
    }
  }
</script>

<style scoped>
  .banner-grid {
    display: grid;
    grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
    gap: 16px;
  }
  .banner-row {
    display: flex;
    align-items: center;
  }
  .banner-hint {
    margin: 8px 0 0;
    color: #71717a;
    font-size: 13px;
  }
  .banner-state {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  .banner-preview {
    border: 1px solid #ececef;
    border-radius: 8px;
    overflow: hidden;
  }
  .banner-preview__bar {
    padding: 8px 12px;
    background: #24323b;
    color: white;
    font-size: 13px;
    text-align: center;
    text-transform: uppercase;
  }
  .banner-preview__app {
    display: flex;
    height: 120px;
    background: #fafafa;
  }
  .banner-preview__menu {
    width: 120px;
    background: #e2e8f0;
  }
  .banner-preview__content {
    flex: 1;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .banner-preview__content div {
    height: 12px;
    border-radius: 4px;
    background: #e2e8f0;
  }
  .banner-preview__content div:nth-child(2) {
    width: 70%;
  }
  .banner-preview__content div:nth-child(3) {
    width: 45%;
  }
  @media (max-width: 900px) {
    .banner-grid {
      grid-template-columns: 1fr;
    }
  }
</style>
